"use client";

import "./AssumptionSummaryTable.css";

export type AssumptionSummaryValueState =
  | "zero"
  | "negative"
  | "unavailable"
  | "pre-payback"
  | "post-payback"
  | "beyond-horizon";

export interface AssumptionSummaryCell {
  /** Preformatted display value. The component does not calculate values. */
  value: string | null;
  /** Explicit display state supplied by the caller. */
  state?: AssumptionSummaryValueState;
  /** Optional replacement for the generated accessible cell description. */
  accessibleLabel?: string;
}

export interface AssumptionSummaryAnnualColumn {
  id: string;
  label: string;
}

export interface AssumptionSummaryRampDisplay {
  label?: string;
  values: Record<string, string | null>;
  total?: string | null;
}

export interface AssumptionSummaryRow {
  id: string;
  label: string;
  /** Controls the visual emphasis of computed subtotal and return rows. */
  kind?: "value-stream" | "total" | "net" | "outcome";
  annualValues: Record<string, AssumptionSummaryCell>;
  total: AssumptionSummaryCell;
  /** When set, the row label opens this source value stream. */
  valueStreamId?: string;
  /**
   * When true, renders one cell spanning all year columns instead of
   * per-year cells. Use for aggregate summary rows such as Aggregate ROI
   * and Payback period where a single combined label makes more sense.
   * Provide the spanning cell content via `spanCell`.
   */
  spanAll?: boolean;
  /** Content for the single spanning cell when `spanAll` is true. */
  spanCell?: AssumptionSummaryCell;
}

export type AssumptionSummaryGroupKind = "benefits" | "costs" | "returns";

export interface AssumptionSummaryGroup {
  id: string;
  kind: AssumptionSummaryGroupKind;
  label: string;
  rows: AssumptionSummaryRow[];
}

export interface AssumptionSummaryTableProps {
  annualColumns: AssumptionSummaryAnnualColumn[];
  groups: AssumptionSummaryGroup[];
  rampDisplay?: AssumptionSummaryRampDisplay;
  onOpenValueStream?: (valueStreamId: string) => void;
}

const STATUS_COPY: Record<AssumptionSummaryValueState, string> = {
  zero: "Zero",
  negative: "Negative",
  unavailable: "Unavailable",
  "pre-payback": "Pre-payback",
  "post-payback": "Post-payback",
  "beyond-horizon": "Beyond horizon",
};

/**
 * A controlled, presentational annual summary of precomputed value streams.
 * Callers provide all formatted values, totals, and value states.
 */
export function AssumptionSummaryTable({
  annualColumns,
  groups,
  rampDisplay,
  onOpenValueStream,
}: AssumptionSummaryTableProps) {
  return (
    <section className="as-summary" aria-label="Assumption summary">
      <div className="as-summary-scroll">
        <table className="as-summary-table">
          <caption className="as-summary-caption">
            Annual value stream summary
          </caption>
          <thead>
            <tr>
              <th scope="col" className="as-summary-label-cell">
                Model summary
              </th>
              {annualColumns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className="as-summary-value-cell"
                >
                  {column.label}
                </th>
              ))}
              <th scope="col" className="as-summary-value-cell">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rampDisplay && (
              <RampRow
                annualColumns={annualColumns}
                rampDisplay={rampDisplay}
              />
            )}
            {groups.map((group) => (
              <GroupRows
                key={group.id}
                group={group}
                annualColumns={annualColumns}
                onOpenValueStream={onOpenValueStream}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RampRow({
  annualColumns,
  rampDisplay,
}: Pick<AssumptionSummaryTableProps, "annualColumns" | "rampDisplay"> & {
  rampDisplay: AssumptionSummaryRampDisplay;
}) {
  const label = rampDisplay.label ?? "Percentage ramp";

  return (
    <tr className="as-summary-ramp-row">
      <th scope="row" className="as-summary-label-cell">
        {label}
      </th>
      {annualColumns.map((column) => (
        <td key={column.id} className="as-summary-value-cell">
          {rampDisplay.values[column.id] ?? "Unavailable"}
        </td>
      ))}
      <td className="as-summary-value-cell">{rampDisplay.total ?? ""}</td>
    </tr>
  );
}

function GroupRows({
  group,
  annualColumns,
  onOpenValueStream,
}: Pick<AssumptionSummaryTableProps, "annualColumns" | "onOpenValueStream"> & {
  group: AssumptionSummaryGroup;
}) {
  return (
    <>
      <tr className={`as-summary-group-row as-summary-group-row-${group.kind}`}>
        <th scope="row" className="as-summary-label-cell">
          {group.label}
        </th>
        {annualColumns.map((column) => (
          <td
            key={column.id}
            className="as-summary-value-cell"
            aria-hidden="true"
          />
        ))}
        <td className="as-summary-value-cell" aria-hidden="true" />
      </tr>
      {group.rows.map((row) => (
        <SummaryRow
          key={row.id}
          row={row}
          annualColumns={annualColumns}
          onOpenValueStream={onOpenValueStream}
        />
      ))}
    </>
  );
}

function SummaryRow({
  row,
  annualColumns,
  onOpenValueStream,
}: Pick<AssumptionSummaryTableProps, "annualColumns" | "onOpenValueStream"> & {
  row: AssumptionSummaryRow;
}) {
  const canOpenValueStream = Boolean(row.valueStreamId && onOpenValueStream);

  return (
    <tr
      className={`as-summary-row as-summary-row-${row.kind ?? "value-stream"}`}
    >
      <th scope="row" className="as-summary-label-cell">
        {canOpenValueStream ? (
          <button
            type="button"
            className="as-summary-stream-button"
            aria-label={`Open ${row.label} value stream`}
            onClick={() => onOpenValueStream?.(row.valueStreamId as string)}
          >
            {row.label}
          </button>
        ) : (
          row.label
        )}
      </th>
      {row.spanAll ? (
        <SummaryCell
          columnLabel="Model summary"
          cell={
            row.spanCell ?? {
              value: null,
              state: "unavailable",
            }
          }
          colSpan={annualColumns.length}
          className="as-summary-span-cell"
        />
      ) : (
        annualColumns.map((column) => (
          <SummaryCell
            key={column.id}
            columnLabel={column.label}
            cell={
              row.annualValues[column.id] ?? {
                value: null,
                state: "unavailable",
              }
            }
          />
        ))
      )}
      <SummaryCell columnLabel="Total" cell={row.total} />
    </tr>
  );
}

function SummaryCell({
  columnLabel,
  cell,
  colSpan,
  className,
}: {
  columnLabel: string;
  cell: AssumptionSummaryCell;
  colSpan?: number;
  className?: string;
}) {
  const status = cell.state ? STATUS_COPY[cell.state] : null;
  const displayValue =
    cell.state === "pre-payback"
      ? "Not yet"
      : (cell.value ??
        (cell.state === "beyond-horizon"
          ? "Beyond horizon"
          : cell.state === "unavailable"
            ? "N/A"
            : ""));
  const accessibleLabel =
    cell.accessibleLabel ??
    [columnLabel, displayValue, status].filter(Boolean).join(": ");

  return (
    <td
      className={`as-summary-value-cell${className ? ` ${className}` : ""}`}
      aria-label={accessibleLabel}
      colSpan={colSpan}
    >
      <span
        className={`as-summary-cell-value${cell.state ? ` as-summary-cell-${cell.state}` : ""}`}
      >
        {displayValue}
      </span>
    </td>
  );
}
